import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Modal } from "../../components/modal";
import {
  getRoleContactFromForm,
  RoleForm,
  RoleFormValues,
} from "../../components/role-form";
import { useGetAssociationMembers } from "../../queries/association-member";
import { useGetRole } from "../../queries/role";
import { updateRole } from "../../services/role";
import { useLocale } from "../../stores/locale";
import { recordIdToId } from "../../utils/record-id-to-id";
import { navigateBack } from "../../utils/navigate-back";

const emptyFormValues: RoleFormValues = {
  name: "",
  description: "",
  externalDescription: "",
  contact: { email: "", phone: "" },
  assignedUserIds: [],
};

export function RoleEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentLanguage } = useLocale();

  if (!id) {
    throw new Error("Role ID is required");
  }

  const { data: role, isLoading: isLoadingRole } = useGetRole(id);
  const { data: members } = useGetAssociationMembers();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [formValues, setFormValues] =
    useState<RoleFormValues>(emptyFormValues);

  useEffect(() => {
    if (role) {
      setFormValues({
        name: role.name ?? "",
        description: role.description ?? "",
        externalDescription: role.externalDescription ?? "",
        contact: {
          email: role.contact?.email ?? "",
          phone: role.contact?.phone ?? "",
        },
        assignedUserIds: role.assignedTo.map((user) => recordIdToId(user.id)),
      });
    }
  }, [role, currentLanguage]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/role");
  };

  return (
    <Modal open onClose={closeModal}>
      <RoleForm
        editorKey={`${role?.id}-${currentLanguage}`}
        hasError={hasError}
        loading={isLoadingRole || loading}
        members={members}
        values={formValues}
        onCancel={closeModal}
        onChange={setFormValues}
        onSubmit={() => {
          void (async () => {
            setLoading(true);
            setHasError(false);
            try {
              await updateRole(
                id,
                {
                  name: formValues.name,
                  description: formValues.description || undefined,
                  externalDescription:
                    formValues.externalDescription || undefined,
                  contact: getRoleContactFromForm(formValues.contact),
                  assignedUserIds: formValues.assignedUserIds,
                },
                currentLanguage,
              );
              await Promise.all([
                queryClient.invalidateQueries({
                  queryKey: ["role", id, currentLanguage],
                }),
                queryClient.invalidateQueries({
                  queryKey: ["roles", currentLanguage],
                }),
              ]);
              closeModal();
            } catch {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
